export interface CompetitiveConfig {
  readonly fixedStepSeconds: number;
  readonly matchDurationSeconds: number;
  readonly arenaRadius: number;
  readonly baseSpeed: number;
  readonly boostSpeedMultiplier: number;
  readonly boostCostPerSecond: number;
  readonly boostFragmentCost: number;
  readonly minimumBoostLength: number;
  readonly snakeWidth: number;
  readonly headRadiusFactor: number;
  readonly bodyRadiusFactor: number;
  readonly growthPerFoodValue: number;
  readonly eliminationFragmentSpacing: number;
  readonly eliminationFragmentValue: number;
  readonly spatialCellSize: number;
  readonly resonancePeriodSeconds: number;
  readonly resonanceBandWidth: number;
  readonly resonanceDurationSeconds: number;
  readonly resonanceValueMultiplier: number;
  readonly botCount: number;
  readonly botPerceptionRadius: number;
  readonly botReactionSeconds: number;
  readonly naturalFoodCount: number;
}

export const DEFAULT_COMPETITIVE_CONFIG: CompetitiveConfig = Object.freeze({
  fixedStepSeconds: 1 / 60,
  matchDurationSeconds: 180,
  arenaRadius: 1_500,
  baseSpeed: 145,
  boostSpeedMultiplier: 1.45,
  boostCostPerSecond: 6,
  boostFragmentCost: 1,
  minimumBoostLength: 80,
  snakeWidth: 14,
  headRadiusFactor: 0.46,
  bodyRadiusFactor: 0.42,
  growthPerFoodValue: 2.5,
  eliminationFragmentSpacing: 14,
  eliminationFragmentValue: 1.5,
  spatialCellSize: 64,
  resonancePeriodSeconds: 20,
  resonanceBandWidth: 64,
  resonanceDurationSeconds: 3,
  resonanceValueMultiplier: 2,
  botCount: 9,
  botPerceptionRadius: 360,
  botReactionSeconds: 0.2,
  naturalFoodCount: 400,
});

const COMPETITIVE_FIELDS = new Set(Object.keys(DEFAULT_COMPETITIVE_CONFIG));

export type ConfigValidation =
  | { readonly ok: true; readonly value: CompetitiveConfig }
  | { readonly ok: false; readonly reason: string };

export const validateCompetitiveConfig = (input: unknown): ConfigValidation => {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return { ok: false, reason: "config-must-be-object" };
  }
  const record = input as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!COMPETITIVE_FIELDS.has(key))
      return { ok: false, reason: `unsupported-field:${key}` };
  }
  for (const key of COMPETITIVE_FIELDS) {
    if (!(key in record)) return { ok: false, reason: `missing-field:${key}` };
    const value = record[key];
    if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
      return { ok: false, reason: `invalid-field:${key}` };
    }
  }
  const botCount = Number(record.botCount);
  const naturalFoodCount = Number(record.naturalFoodCount);
  if (!Number.isInteger(botCount) || !Number.isInteger(naturalFoodCount)) {
    return { ok: false, reason: "counts-must-be-integers" };
  }
  if (botCount < 8 || botCount > 10) {
    return { ok: false, reason: "bot-count-out-of-range" };
  }
  return {
    ok: true,
    value: Object.freeze({ ...record }) as unknown as CompetitiveConfig,
  };
};

export const mergeCompetitiveConfig = (
  overrides: Partial<CompetitiveConfig> = {},
): CompetitiveConfig => {
  const candidate = { ...DEFAULT_COMPETITIVE_CONFIG, ...overrides };
  const validation = validateCompetitiveConfig(candidate);
  if (!validation.ok) throw new Error(validation.reason);
  return validation.value;
};
